import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import {
    AddButton,
    CircularLoading
} from '../../Common';
import ToolBar from '../DataGridToolbar/index.js';
import {
    TablePagination,
    tablePaginationClasses,
    Box,
    IconButton,
    Tooltip,
    Card
} from '@mui/material';

import { DataGridPro, useGridApiRef, gridClasses } from '@mui/x-data-grid-pro';

import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import UserInfo from '../../../UserInfo';
import { trakrefFontFamilySemiBold } from '../../../utils';
import {
    getDefaultPage,
    getDefaultSortBy,
    getSortFromSavedState,
    generateFilterModel,
    generateSortModel,
    parseSortByToSortModel,
    saveTableState,
    ColumnMenu,
    getTogglableColumns,
    NoData,
    generateColumn,
    getLocalStorageKey,
    validateAndCleanCachedState
} from './utils.js';
import NoDataTableIcon from '../../../Icons/empty_table_placeholder.svg';
import '../Table/Table.css';
import { trakrefBlue } from '../../../color_utils.js';
import { isMobile } from 'react-device-detect';
import { useScrollToElement } from '../../../hooks/useScrollToElement.js';


const DataGrid = ({
    tableId,
    url,
    defaultSort,
    rowCount,
    page,
    refresh,
    exportButtonURL,
    exportButtonAlt,
    exportFileName,
    noResultText,
    noResultText2,
    noResultText3,
    tableHeader,
    filterOptions,
    hideExportButton,
    hideFilterButton,
    columns,
    exportFileTypes,
    headerContent,
    filterValues,
    MUIButtonText,
    MUIButtonOnClick,
    showAddButton,
    dataGridSx,
    renderEditButton,
    editOnClick,
    editViewColumns,
    staticFilters,
    toolbarLeftContent
}) => {
    const gridApiRef = useGridApiRef();
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(rowCount);
    const [initialColumns, setInitialColumns] = useState({});
    const [initialState, setInitialState] = useState({});
    const [resultCount, setResultCount] = useState(0);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [editActive, setEditActive] = useState(false);
    const [hasUnsavedRows, setHasUnsavedRows] = useState(false);
    const [topRef, scrollToTop] = useScrollToElement();
    const unsavedChangesRef = useRef({
        unsavedRows: [],
        rowsBeforeChange: [],
    });

    const processRowUpdate = useCallback((newRow, oldRow) => {
        // console.log('new row', newRow);
        // console.log('old row', oldRow)

        const changesExist = !isEqual(newRow, oldRow);
        
        if (!changesExist) {
            return oldRow;
        }
        
        const rowId = newRow.id;
        const unsavedRowIds = unsavedChangesRef.current.rowsBeforeChange.map(r => r.id);
        
        unsavedChangesRef.current.unsavedRows.push(newRow);
        if (!unsavedRowIds.includes(rowId)) {
            unsavedChangesRef.current.rowsBeforeChange.push(oldRow);
        }
        
        setHasUnsavedRows(true);
        return newRow;
    }, []);

    const discardChanges = useCallback(() => {
        setHasUnsavedRows(false);
        gridApiRef.current?.updateRows(unsavedChangesRef.current.rowsBeforeChange);
        unsavedChangesRef.current = {
            unsavedRows: [],
            rowsBeforeChange: [],
        };
    }, [gridApiRef]);

    const saveChanges = useCallback(async () => {
        try {
            setTableLoading(true);
            
            // unsavedRows = list of objects, each item in list is edited row
            await editOnClick(unsavedChangesRef.current.unsavedRows);

            unsavedChangesRef.current = {
                unsavedRows: [],
                rowsBeforeChange: [],
            };
        } catch (error) {
            setTableLoading(false);
        } finally {
            setHasUnsavedRows(false);
            setEditActive(false);
            await fetchData();
        }
    }, [gridApiRef]);

    const getRowClassName = useCallback((params) => {
        if (editActive) return '';
        const { indexRelativeToCurrentPage } = params;
        return indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row';
    }, [editActive]);

    const getCellClassName = useCallback((params) => {
        const { id } = params;
        const unsavedCell = unsavedChangesRef.current.unsavedRows.map(r => r.id).includes(id);
        if (editActive && unsavedCell) {
            return 'cell--edited';
        }
        
        return '';
    }, [editActive]);

    const LOCAL_STORAGE_TABLE_STATE = getLocalStorageKey(url);

    const memoizedColumns = useMemo(() => {
        const cachedState = validateAndCleanCachedState(LOCAL_STORAGE_TABLE_STATE, columns);
        const orderedFields = cachedState?.columns?.orderedFields;

        if (Array.isArray(orderedFields) && orderedFields.length > 0) {
            const orderedFieldSet = new Set(orderedFields);

            const orderedColumns = orderedFields
                .map(field => columns.find(col => col.field === field))
                .filter(col => col !== undefined)
                .map(col => generateColumn(col, filterOptions));

            // Append columns added after the cache was saved (e.g. Quick Actions)
            const newColumns = columns
                .filter(col => !orderedFieldSet.has(col.field))
                .map(col => generateColumn(col, filterOptions));

            return [...orderedColumns, ...newColumns];
        }

        return columns.map(col => generateColumn(col, filterOptions));
    }, [filterOptions, columns]);
    
    const getColumns = () => {
        if (editActive) {
            return editViewColumns.map(col => generateColumn(col, filterOptions));
        }
        
        return memoizedColumns;
    };

    const [tableRequest, setTableRequest] = useState({
        page: getDefaultPage(url, page),
        sortBy: getSortFromSavedState(LOCAL_STORAGE_TABLE_STATE, getDefaultSortBy(url, defaultSort)),
        filters: (() => {
            const cachedState = validateAndCleanCachedState(LOCAL_STORAGE_TABLE_STATE, columns);
            return generateFilterModel(cachedState?.filter?.filterModel?.items);
        })()
    });

    useEffect(() => {
        const cachedState = validateAndCleanCachedState(LOCAL_STORAGE_TABLE_STATE, columns);
        let columnInitialVisibility = {};

        if (cachedState?.filter?.filterModel) {
            setInitialState(cachedState);
            setFilterModel(cachedState.filter.filterModel);
        }

        columns.forEach(({ field, initiallyVisible = true }) => (
            columnInitialVisibility[field] = initiallyVisible
        ));

        setInitialColumns(columnInitialVisibility);
    }, []);

    useEffect(() => {
        if (filterValues) {
            const data = {
                items: filterValues
            }
            filterModelChange(data)
        }
    }, [filterValues, tableRequest.filters])

    useEffect(() => {
        setTableLoading(true);
        fetchData();
    }, [tableRequest, refresh, url, JSON.stringify(staticFilters)]);

    // Activate edit mode - 1st editable field in 1st row
    useEffect(() => {
        if (!editActive) return;
        const ids = gridApiRef.current?.getSortedRowIds() ?? [];
        const editableFieldsTest = gridApiRef
            .current
            .getVisibleColumns()
            .filter(c => c.editable);
        const [{ field }] = editableFieldsTest;
        if (ids.length) {
            gridApiRef.current.startCellEditMode({ id: ids[0], field });
        }
    }, [editActive]);

    const fetchData = async () => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    rowCount: rowsPerPage,
                    page: tableRequest.page,
                    filters: [...staticFilters, ...tableRequest.filters],
                    sortBy: tableRequest.sortBy
                })
            });

            const data = await response.json();
            const { rows, resultCount } = data;

            if ((!rows || rows.length <= 0) && tableRequest.page > 1) {
                return changePage(-1)
            }

            setRows(rows);
            setResultCount(resultCount);
        }
        catch {
            setLoading(false);
        }
        finally {
            setLoading(false);
            setTableLoading(false);
        }
    };

    const changePage = (amount) => {
        setTableLoading(true);
        sessionStorage.setItem(url + "page" + UserInfo.instanceId, JSON.stringify(tableRequest.page + amount));
        setTableRequest(prev => ({
            ...prev,
            page: prev.page + amount
        }));
    };
    const handleChangeRowsPerPage = (event) => {
        setTableLoading(true);
        setRowsPerPage(parseInt(event.target.value, 10));
        setTableRequest(prev => ({ ...prev, page: 1 }));
    };

    const handleFirstPageButtonClick = () => {
        scrollToTop();
        changePage(-tableRequest.page + 1);
    }

    const handleLastPageButtonClick = () => {
        scrollToTop();
        changePage(Math.max(0, Math.ceil(resultCount / rowsPerPage) - tableRequest.page))
    }

    const changePageBack = () => {
        scrollToTop();
        changePage(-1);
    }

    const changePageForward = () => {
        scrollToTop();
        changePage(1);
    }

    const PaginationActions = () => (
        <Box sx={{ flexShrink: 0 }}>
            <Tooltip title='First Page'>
                <IconButton
                    onClick={handleFirstPageButtonClick}
                    disabled={tableRequest.page === 1 || tableLoading}
                    aria-label="first page"
                    sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
                >
                    <FirstPageIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title='Previous Page'>
                <IconButton
                    onClick={changePageBack}
                    disabled={tableRequest.page === 1 || tableLoading}
                    aria-label="previous page"
                >
                    <KeyboardArrowLeft />
                </IconButton>
            </Tooltip>
            <Tooltip title='Next Page'>
                <IconButton
                    onClick={changePageForward}
                    disabled={tableRequest.page > Math.ceil(resultCount / rowsPerPage) - 1 || tableLoading}
                    aria-label="next page"
                >
                    <KeyboardArrowRight />
                </IconButton>
            </Tooltip>
            <Tooltip title='Last Page'>
                <IconButton
                    onClick={handleLastPageButtonClick}
                    disabled={tableRequest.page > Math.ceil(resultCount / rowsPerPage) - 1 || tableLoading}
                    aria-label="last page"
                    sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
                >
                    <LastPageIcon />
                </IconButton>
            </Tooltip>
            {/* Input field for page number - might need this in the future */}
            {/* <Input
                label='Page'
                variant='standard'
                value={tableRequest.page}
                onChange={e => {
                    // changePage(-tableRequest.page + 1 + Number(e.target.value))
                    console.log('value', e.target.value)
                }}
            /> */}
        </Box>
    );

    const Pagination = () => (
        <TablePagination
            sx={{
                [`& .${tablePaginationClasses.selectLabel}`]: { fontFamily: trakrefFontFamilySemiBold, display: { xs: 'none', lg: 'block' } },
                [`& .${tablePaginationClasses.displayedRows}`]: { fontFamily: trakrefFontFamilySemiBold },
                [`& .${tablePaginationClasses.spacer}`]: { display: "none" },
                [`& .${tablePaginationClasses.toolbar}`]: { justifyContent: "center", paddingLeft: 2 },
                ...(tableHeader && {
                    border: 'none'
                }),
                [`& .MuiTablePagination-input`]: { display: { xs: 'none', lg: 'inline-flex' } },
                [`& .${tablePaginationClasses.selectIcon}`]: { display: { xs: 'none', lg: 'block' } }
            }}
            rowsPerPageOptions={[5, 10, 50, 100]}
            count={resultCount}
            rowsPerPage={rowsPerPage}
            // MUI TablePagination is built using a starting index of 0
            // we start at 1, so must subtract 1 to see proper results 
            page={tableRequest.page - 1}
            slotProps={{
                select: {
                    inputProps: {
                        'aria-label': 'rows per page',
                    },
                },
            }}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={PaginationActions}
        />
    );

    const clearAllFilters = (newFilterModel) => {
        setFilterModel(newFilterModel);
        setTableRequest(prev => ({
            ...prev,
            page: 1,
            filters: []
        }));
        saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE);
    };

    // Construct filters array and append to table query
    const filterModelChange = (newFilterModel) => {
        const { items } = newFilterModel;
        setFilterModel(newFilterModel);

        // Clearing all filters
        if (items.length <= 0) {
            return clearAllFilters(newFilterModel);
        }

        const filters = generateFilterModel(items);
        console.log('filters', filters);

        setTableLoading(true);
        setTableRequest(prev => ({ ...prev, page: 1, filters }));
        saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE);
    };

    const handleFilterModelChange = useCallback(filterModelChange, []);

    const handleSortModelChange = useCallback((newSortModel) => {
        const sortBy = generateSortModel(newSortModel);

        setTableLoading(true);
        setTableRequest(prev => ({ ...prev, page: 1, sortBy }));
        saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE);
    }, []);

    if (loading) {
        return (
            <CircularLoading size={50} marginTop={3} />
        );
    }

    return (
        <Card
            variant='outlined'
            style={{ height: rows.length > 0 ? "auto" : MUIButtonOnClick ? "520px" : "500px" }}
        >
            <div ref={topRef} />
            {headerContent && headerContent}
            <DataGridPro
                pagination
                disableRowSelectionOnClick
                apiRef={gridApiRef}
                rows={rows}
                columns={getColumns()}
                loading={tableLoading}
                columnHeaderHeight={45}
                filterMode='server'
                sortingMode='server'
                onFilterModelChange={handleFilterModelChange}
                onSortModelChange={handleSortModelChange}
                isCellEditable={() => editActive}
                processRowUpdate={processRowUpdate}
                localeText={{
                    toolbarExportCSV: exportButtonAlt,
                    toolbarColumnsLabel: 'Customize Columns',
                    "headerFilterOperatorFrom-to": "From - To",
                    "headerFilterOperatorBetween": "From - To",
                }}
                slots={{
                    pagination: Pagination,
                    toolbar: () => (
                        <Box display="flex" justifyContent="space-between" sx={{ py: { xs: '22px', lg: 0 } }}>
                            <ToolBar
                                enableExport
                                exportButtonTooltip={exportButtonAlt}
                                exportFileTypes={exportFileTypes}
                                exportOptions={{
                                    ...tableRequest,
                                    filters: [...staticFilters, ...tableRequest.filters],
                                    exportFileName,
                                    exportURL: exportButtonURL,
                                }}
                                hideExportButton={hideExportButton}
                                hideFilterButton={hideFilterButton}

                                toolbarLeftContent={toolbarLeftContent}
                                renderEditButton={renderEditButton}
                                editOnClick={saveChanges}
                                cancelOnClick={discardChanges}
                                editActive={editActive}
                                saveDisabled={!hasUnsavedRows}
                                setEditActive={setEditActive}
                            />
                            {MUIButtonOnClick && showAddButton && rows.length > 0 &&(
                                <AddButton text={MUIButtonText} onClick={MUIButtonOnClick} sx={{ mt: 2, mr: { xs: 0, sm: 1 }, width: { xs: '100%', sm: 'auto' } }} />
                            )}
                        </Box>
                    ),
                    noRowsOverlay: () => (
                        <NoData
                            noTableDataIcon={NoDataTableIcon}
                            noResultText={noResultText}
                            noResultText2={noResultText2}
                            noResultText3={noResultText3}
                            tableRequest={tableRequest}
                            MUIButtonText={MUIButtonText}
                            MUIButtonOnClick={MUIButtonOnClick}
                        />
                    ),
                    columnMenu: ColumnMenu,
                    headerFilterMenu: null
                }}
                slotProps={{
                    columnsManagement: {
                        getTogglableColumns: (columns) => getTogglableColumns(columns, 'Quick Actions'),
                        disableResetButton: true
                    },
                    loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'linear-progress',
                    },
                    filterPanel: {
                        logicOperators: isMobile ? [] : ['and'],
                        sx: isMobile ? {
                            width: '100%',
                            "& .MuiDataGrid-filterFormOperatorInput": {
                                width: '100px'
                            }
                        } : {},
                    }
                }}
                initialState={{
                    // initially show predetermined columns
                    columns: { columnVisibilityModel: initialColumns },
                    // spread initialState afterwards to load saved table modifications
                    ...initialState,
                }}
                filterModel={filterModel}
                sortModel={parseSortByToSortModel(tableRequest.sortBy)}
                getRowClassName={getRowClassName}
                getCellClassName={getCellClassName}
                onColumnVisibilityModelChange={() => saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE)}
                onColumnOrderChange={() => saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE)}
                filterDebounceMs={350}
                sx={{
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: trakrefBlue,
                    },
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                        outline: `2px solid ${trakrefBlue}`
                    },
                    '& .MuiDataGrid-menuIcon': {
                        visibility: 'visible',
                        width: 'auto',
                        opacity: 1,
                    },
                    '& .MuiDataGrid-footerContainer': {
                        justifyContent: { xs: 'flex-start', lg: 'center' },
                    },
                    ...(
                        tableHeader ? {
                            borderTopLeftRadius: '0px',
                            borderTopRightRadius: '0px',
                            borderBottom: 'none',
                        } : {
                            border: 'none',
                        }
                    ),
                    '& .MuiDataGrid-toolbarContainer .MuiButton-root[aria-label*="Export"]': {
                        display: { xs: 'none', lg: 'inline-flex' }
                    },
                    [`& .${gridClasses['cell--editable']}.cell--edited`]: {
                        backgroundColor: () => 'rgba(255, 254, 176, 0.3)',
                    },
                    ...dataGridSx
                }}
            />
        </Card>
    );
};

DataGrid.propTypes = {
    tableId: PropTypes.string,
    url: PropTypes.string.isRequired,
    page: PropTypes.number,
    rowCount: PropTypes.number,
    defaultSort: PropTypes.string,
    refresh: PropTypes.bool,
    exportButtonURL: PropTypes.string,
    exportButtonAlt: PropTypes.string,
    exportFileName: PropTypes.string,
    noResultText: PropTypes.string,
    noResultText2: PropTypes.string,
    noResultText3: PropTypes.string,
    tableHeader: PropTypes.string,
    filterOptions: PropTypes.array,
    subTableColumns: PropTypes.array,
    hideExportButton: PropTypes.bool,
    hideFilterButton: PropTypes.bool,
    exportFileTypes: PropTypes.array,
    getDetailPanelContent: PropTypes.func,
    getDetailPanelHeight: PropTypes.func,
    headerContent: PropTypes.element,
    filterValues: PropTypes.array,
    enableExport: PropTypes.bool,
    dataGridSx: PropTypes.object,
    showAddButton: PropTypes.bool,
    renderEditButton: PropTypes.bool,
    editOnClick: PropTypes.func,
    editViewColumns: PropTypes.array,
    staticFilters: PropTypes.array
};

DataGrid.defaultProps = {
    page: 1,
    rowCount: 10,
    defaultSort: "",
    refresh: false,
    exportButtonURL: "",
    exportButtonAlt: "",
    exportFileName: "",
    noResultText: "",
    noResultText2: "",
    noResultText3: "",
    tableHeader: "",
    filterOptions: [],
    subTableColumns: [],
    hideExportButton: false,
    hideFilterButton: false,
    exportFileTypes: [{ type: 'csv', name: 'CSV' }],
    headerContent: null,
    filterValues: null,
    enableExport: true,
    showAddButton: false,
    renderEditButton: false,
    editOnClick: async () => {},
    editViewColumns: [],
    staticFilters: []
};

export default DataGrid;
